import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Star, X, MessageSquare } from "lucide-react";
import useModalA11y from "@/lib/useModalA11y";

const HEADERS = () => ({ Authorization: `Bearer ${localStorage.getItem("fl_token") || ""}` });

/**
 * Patient testimonial capture. Opens when `?feedback=<appointment_id>`
 * appears in the URL (fired from the "testimonial_request" notification
 * that gets emitted every time a clinician completes an appointment).
 *
 * Mount this once inside DashLayout for patients only.
 */
export default function TestimonialModal() {
  const loc = useLocation();
  const nav = useNavigate();
  const q = new URLSearchParams(loc.search);
  const appointmentId = q.get("feedback");
  const [rating, setRating] = useState(5);
  const [quote, setQuote] = useState("");
  const [share, setShare] = useState(true);
  const [meta, setMeta] = useState({ clinician_name: "", service: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!appointmentId) return;
    // Best-effort: look up the notification meta so we can show the clinician name.
    axios.get(`${API}/notifications?limit=50`, { headers: HEADERS() })
      .then((r) => {
        const match = (r.data || []).find(
          (n) => n.kind === "testimonial_request" && n.meta?.appointment_id === appointmentId,
        );
        if (match) setMeta(match.meta || {});
      })
      .catch(() => {});
    setRating(5); setQuote(""); setShare(true);
  }, [appointmentId]);

  const close = () => {
    const params = new URLSearchParams(loc.search);
    params.delete("feedback");
    nav({ pathname: loc.pathname, search: params.toString() ? `?${params}` : "" }, { replace: true });
  };
  const dialogRef = useModalA11y({ open: !!appointmentId, onClose: close });

  if (!appointmentId) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (quote.trim().length < 10) {
      toast.error("Share a little more — at least a sentence.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/testimonials`,
        {
          rating,
          quote: quote.trim(),
          context: meta?.service || undefined,
          clinician_name: meta?.clinician_name || undefined,
          appointment_id: appointmentId,
          share_publicly: share,
        },
        { headers: HEADERS() },
      );
      toast.success(share ? "Thanks — we'll feature this soon!" : "Thanks for the feedback.");
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,.66)", backdropFilter: "blur(6px)", zIndex: 9998 }}
      onClick={close}
      data-testid="testimonial-modal-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="testimonial-title"
        className="fl-card p-6 md:p-8 w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="testimonial-modal"
      >
        <button
          onClick={close}
          data-testid="testimonial-close"
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 rounded-md flex items-center justify-center hover:bg-white/5"
          style={{ color: "var(--fl-muted)" }}
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <MessageSquare size={16} className="text-[color:var(--fl-orange)]" />
          <div className="fl-label text-[color:var(--fl-orange)]">Session feedback</div>
        </div>
        <h2 id="testimonial-title" className="fl-heading text-2xl">How was your session?</h2>
        {(meta?.clinician_name || meta?.service) && (
          <p className="text-xs mt-2" style={{ color: "var(--fl-muted)" }}>
            {[meta?.service, meta?.clinician_name && `with ${meta.clinician_name}`].filter(Boolean).join(" · ")}
          </p>
        )}

        <form onSubmit={submit} className="mt-5 space-y-4">
          {/* Star picker */}
          <div className="flex gap-1" data-testid="testimonial-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                aria-label={`${n} star${n === 1 ? "" : "s"}`}
                data-testid={`testimonial-star-${n}`}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  size={26}
                  className={n <= rating ? "text-[color:var(--fl-orange)] fill-[color:var(--fl-orange)]" : ""}
                  style={{ color: n > rating ? "var(--fl-muted-2)" : undefined }}
                />
              </button>
            ))}
          </div>

          <textarea
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            placeholder="A quick line about your session — what worked, what changed, how you feel."
            rows={4}
            maxLength={800}
            className="fl-input w-full text-sm px-3 py-2 rounded-md"
            data-testid="testimonial-quote"
          />

          <label className="flex items-start gap-2 text-xs cursor-pointer" style={{ color: "var(--fl-muted)" }}>
            <input
              type="checkbox"
              checked={share}
              onChange={(e) => setShare(e.target.checked)}
              className="mt-0.5"
              data-testid="testimonial-share-toggle"
            />
            <span>
              Feature this on our public testimonials page (first name & context only — you can withdraw at any time).
            </span>
          </label>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={close}
              className="fl-btn-secondary text-xs flex-1"
              data-testid="testimonial-skip"
            >
              Maybe later
            </button>
            <button
              type="submit"
              disabled={busy}
              className="fl-btn text-xs flex-1"
              data-testid="testimonial-submit"
            >
              {busy ? "Sending…" : "Send feedback"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
